import { queryOptions, useQuery } from '@tanstack/react-query'

import { getIsLoggedIn } from '../../auth/utils/getIsLoggedIn'
import { communityApi } from '../api/communityApi'
import { COMMUNITY_ACTIVITY, COMMUNITY_LIFETIME } from '../models/community.constants'
import { getComputedSubscription, type TComputedSubscription } from '../utils/getComputedSubscription'

type TEverlastingSubscriptionsOptions<T = TComputedSubscription[]> = { select?: (data: TComputedSubscription[]) => T }

export const getEverlastingSubscriptionsOptions = <T = TComputedSubscription[]>({
  select,
}: TEverlastingSubscriptionsOptions<T> = {}) =>
  queryOptions({
    queryKey: ['everlasting_subscriptions'],
    queryFn: ({ signal }) =>
      communityApi.getSubscriptions(
        {
          exists: {
            leftAt: false,
          },
          'community.lifetime': [COMMUNITY_LIFETIME.EVERLASTING],
          'community.publications.limit': 1,
          'community.publications.order': { effectivePublicationDate: 'desc' },
          'community.activity': [
            COMMUNITY_ACTIVITY.ACTIVE,
            COMMUNITY_ACTIVITY.COMING_SOON,
            COMMUNITY_ACTIVITY.ONGOING,
            COMMUNITY_ACTIVITY.TO_REDISCOVER,
          ],
        },
        { signal },
      ),
    select: (subscriptions) => {
      const computedSubscriptions = subscriptions.member.map(getComputedSubscription)
      return (select ? select(computedSubscriptions) : computedSubscriptions) as T
    },
    staleTime: 15 * 1000,
    enabled: getIsLoggedIn(),
  })

export const useEverlastingSubscriptionsQuery = <T = TComputedSubscription[]>(
  options: TEverlastingSubscriptionsOptions<T> = {},
) => useQuery(getEverlastingSubscriptionsOptions<T>(options))
